import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FullscreenError from '@/components/utils/FullscreenError';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import EditEventSeriesModal from '@/components/admin/events/EditEventSeriesModal';
import EditEventModal from '@/components/admin/events/EditEventModal';
import EventScopeDialog from '@/components/admin/events/EventScopeDialog';
import { faSpinner, faArrowLeft, faRepeat, faEdit, faTrash, faArrowsRotate, faExternalLinkAlt } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatRFCDate } from '@/utils/helpers';
import { describeRecurrenceRule, formatEventDateTime, formatReminderOffsetList } from '@/utils/event-helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Event, EventID, EventSeries, EventSeriesID, EventSeriesScope, FeatureType } from '@/models';
import { FEATURE_TYPE_LABELS, EVENT_RECURRENCE_TYPE_LABELS } from '@/models';

const PAGE_SIZE = 50;

export default function AdminEventSeriesDetail() {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();
  const [series, setSeries] = useState<EventSeries | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState(false);
  const [isEditSeriesModalOpen, setIsEditSeriesModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [scopeDeleteEvent, setScopeDeleteEvent] = useState<Event | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<EventID | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchSeriesAndEvents = useCallback(async () => {
    if (seriesId === undefined) {
      console.error('No seriesId provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const [seriesResponse, eventsResponse] = await Promise.all([
        APIRoutes.getEventSeries(seriesId as EventSeriesID),
        APIRoutes.listEvents({
          seriesID: seriesId as EventSeriesID,
          pageSize: PAGE_SIZE,
          next: currentPage > 1 ? `$${currentPage}` : undefined
        })
      ]);

      setSeries(seriesResponse);
      setEvents(eventsResponse.events);
      setTotalEvents(eventsResponse.total);
    } catch (err) {
      console.error('Critical error loading event series:', err);
      setError('Die Event-Serie konnte nicht geladen werden');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [seriesId, currentPage]);

  useEffect(() => {
    void fetchSeriesAndEvents();
  }, [fetchSeriesAndEvents]);

  const handleSeriesUpdated = (updated: EventSeries, _updatedEvents: Event[], regenerated: boolean) => {
    setSeries(updated);
    showSuccessToast(
      regenerated
        ? 'Serie gespeichert — zukünftige Termine wurden neu aufgebaut'
        : 'Serie gespeichert'
    );
    // The response only carries the future events, so reload for the full (paged) picture
    void fetchSeriesAndEvents();
  };

  const handleRegenerate = async () => {
    if (series === null) return;

    if (!confirm(`Alle zukünftigen Termine von „${series.subject}" werden gelöscht und aus der aktuellen Regel neu erzeugt. Einzeln angepasste Termine verlieren dabei ihre Anpassungen. Vergangene Termine bleiben erhalten.\n\nFortfahren?`)) {
      return;
    }

    try {
      setBusy(true);
      const response = await APIRoutes.regenerateEventSeries(series.id);
      setSeries(response.series);
      showSuccessToast(`${response.events.length} Termin(e) neu erzeugt`);
      await fetchSeriesAndEvents();
    } catch (err) {
      console.error('Error regenerating event series:', err);
      handleAPIError(err, 'Fehler beim Neuaufbau der Serie');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSeries = async () => {
    if (series === null) return;

    if (!confirm(`Serie „${series.subject}" wirklich löschen? Zukünftige Termine werden dabei entfernt.`)) {
      return;
    }

    // Past occurrences are kept by default so the calendar history stays intact
    const includePast = confirm('Sollen auch die bereits vergangenen Termine gelöscht werden?\n\nOK = auch Vergangenheit löschen\nAbbrechen = Vergangenheit behalten');

    try {
      setBusy(true);
      await APIRoutes.deleteEventSeries(series.id, includePast ? true : undefined);
      showSuccessToast('Serie gelöscht');
      navigate('/admin/events?tab=series');
    } catch (err) {
      console.error('Error deleting event series:', err);
      handleAPIError(err, 'Fehler beim Löschen der Serie');
      setBusy(false);
    }
  };

  const handleEventUpdated = () => {
    showSuccessToast('Termin erfolgreich aktualisiert');
    void fetchSeriesAndEvents();
  };

  const deleteEvent = async (eventId: EventID, scope: EventSeriesScope) => {
    try {
      setDeletingEvent(eventId);
      await APIRoutes.deleteEvent(eventId, scope);
      showSuccessToast(scope === 'SINGLE' ? 'Termin gelöscht' : 'Termine der Serie gelöscht');

      // scope=ALL deletes the series itself, so there is nothing left to show here
      if (scope === 'ALL') {
        navigate('/admin/events?tab=series');
        return;
      }

      await fetchSeriesAndEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      handleAPIError(err, 'Fehler beim Löschen des Termins');
    } finally {
      setDeletingEvent(null);
      setScopeDeleteEvent(null);
    }
  };

  if (criticalError) {
    return (
      <FullscreenError
        title="Event-Serie nicht verfügbar"
        message={error ?? 'Die Event-Serie konnte nicht geladen werden.'}
        onRetry={() => void fetchSeriesAndEvents()}
        onBack={() => navigate('/admin/events?tab=series')}
      />
    );
  }

  if (loading && series === null) {
    return (
      <div className="flex justify-center items-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
      </div>
    );
  }

  if (series === null) {
    return null;
  }

  const features: FeatureType[] = series.featureFilter?.features ?? [];

  return (
    <>
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin/events?tab=series"
            className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faRepeat} className="w-8 h-8 text-purple-600 dark:text-purple-400 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {series.subject}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {describeRecurrenceRule(series.rule, series.until)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditSeriesModalOpen(true)}
            disabled={!isAdmin || busy}
            title={isAdmin ? 'Serie bearbeiten' : 'Nur für Administratoren'}
            className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors flex items-center disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faEdit} className="w-4 h-4 mr-2" />
            Bearbeiten
          </button>
          <button
            onClick={() => void handleRegenerate()}
            disabled={!isAdmin || busy}
            title={isAdmin ? 'Zukünftige Termine neu aufbauen' : 'Nur für Administratoren'}
            className={`px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors flex items-center disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faArrowsRotate} className="w-4 h-4 mr-2" />
            Neu aufbauen
          </button>
          <button
            onClick={() => void handleDeleteSeries()}
            disabled={!isAdmin || busy}
            title={isAdmin ? 'Serie löschen' : 'Nur für Administratoren'}
            className={`px-4 py-2 bg-red-600 text-white rounded-lg transition-colors flex items-center disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:bg-red-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faTrash} className="w-4 h-4 mr-2" />
            Löschen
          </button>
        </div>
      </div>

      {/* Overview */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Rhythmus</p>
            <p className="text-sm text-gray-900 dark:text-white">{EVENT_RECURRENCE_TYPE_LABELS[series.rule.type]}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Intervall: alle {series.rule.interval}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Zeitraum</p>
            <p className="text-sm text-gray-900 dark:text-white">
              {series.firstOccurrence !== null ? formatEventDateTime(series.firstOccurrence) : '-'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              bis {series.lastOccurrence !== null ? formatEventDateTime(series.lastOccurrence) : '-'} (Serienende {formatRFCDate(series.until)})
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Termine</p>
            <p className="text-sm text-gray-900 dark:text-white">{series.occurrenceCount}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Zielgruppe</p>
            {series.featureFilter === null ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Öffentlich
              </span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {features.map((f) => (
                  <span key={f} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    {FEATURE_TYPE_LABELS[f]}
                  </span>
                ))}
                {series.featureFilter.onlyCommunityMembers && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    nur Community
                  </span>
                )}
                {series.featureFilter.excludeCommunityMembers && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                    ohne Community
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Erinnerungen</p>
            {(series.reminderMinutes ?? []).length > 0 ? (
              <p className="text-sm text-gray-900 dark:text-white">
                {formatReminderOffsetList(series.reminderMinutes ?? [])} vor Beginn
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Keine</p>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Link</p>
            {series.link.length > 0 ? (
              <a
                href={series.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-purple-600 dark:text-purple-400 hover:underline inline-flex items-center break-all"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3 h-3 mr-1.5 shrink-0" />
                {series.link}
              </a>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
            )}
          </div>
          {series.description.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Beschreibung</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{series.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Occurrences */}
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Termine der Serie ({totalEvents})
      </h2>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
        </div>
      )}

      {!loading && events.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 shadow-md rounded-lg">
          <p className="text-gray-600 dark:text-gray-400">
            Diese Serie hat aktuell keine Termine.
          </p>
        </div>
      )}

      {!loading && events.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Betreff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Start
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ende
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {event.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatEventDateTime(event.start)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatEventDateTime(event.end)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {event.seriesOverridden ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
                          title="Dieser Termin wurde einzeln angepasst und wird von Änderungen an der Serie nicht mehr überschrieben"
                        >
                          einzeln angepasst
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-600">aus der Serie</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingEvent(event)}
                          disabled={!isAdmin || deletingEvent === event.id}
                          className={`text-blue-600 dark:text-blue-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer'}`}
                          title={isAdmin ? 'Bearbeiten' : 'Nur für Administratoren'}
                        >
                          <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setScopeDeleteEvent(event)}
                          disabled={!isAdmin || deletingEvent === event.id}
                          className={`text-red-600 dark:text-red-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-red-900 dark:hover:text-red-300 cursor-pointer'}`}
                          title={isAdmin ? 'Löschen' : 'Nur für Administratoren'}
                        >
                          {deletingEvent === event.id ? (
                            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                          ) : (
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PageBasedPagination
            currentPage={currentPage}
            totalCount={totalEvents}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}

      {/* Mounted only while open, so the form is always seeded from the current series */}
      {isEditSeriesModalOpen && (
        <EditEventSeriesModal
          isOpen={true}
          onClose={() => setIsEditSeriesModalOpen(false)}
          series={series}
          onSeriesUpdated={handleSeriesUpdated}
        />
      )}

      {editingEvent !== null && (
        <EditEventModal
          isOpen={true}
          onClose={() => setEditingEvent(null)}
          event={editingEvent}
          onEventUpdated={handleEventUpdated}
        />
      )}

      {scopeDeleteEvent !== null && (
        <EventScopeDialog
          isOpen={true}
          onClose={() => setScopeDeleteEvent(null)}
          title="Termin löschen"
          description={`„${scopeDeleteEvent.subject}" gehört zu dieser Serie. Welche Termine sollen gelöscht werden?`}
          confirmLabel="Löschen"
          onConfirm={(scope) => void deleteEvent(scopeDeleteEvent.id, scope)}
          loading={deletingEvent === scopeDeleteEvent.id}
        />
      )}
    </>
  );
}
